import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, User, Euro, Clock, ChevronRight, CheckCircle, Users, CreditCard, Mail, ReceiptText, Calendar, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { Lesson, LedgerEntry, Student } from '../components/interfaces/All';

const Ledger: React.FC = () => {
  const navigate = useNavigate();
  const { state, fetchMyLedger, fetchLessons, fetchTeachers } = useSupabase();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>();

  // const teacherId = '0ee08853-a2f3-49de-a012-9c8033288e4c' //state.userProfile?.id as string; // Current teacher's ID

  let ledger = state.ledger || [];
  let lessons = state.lessons || [];
  let students = state.students || [];

  switch (state.userRole){
    case('student'):
      lessons = lessons.filter(l=>l.student_id == state.userProfile.id);
      ledger = ledger.filter(l=>l.student_id == state.userProfile.id);
      students = students.filter(s=>s.id == state.userProfile.id);
    break;
    // case('teacher'):
    // break;
    // case('admin'):
    // break;
  }
  //

  const pricePerH = 10;
  // Filter everything by teacher ID
  const teacherLessons = useMemo(() =>
    lessons,//.filter(lesson => lesson.teacher === teacherId),
    [lessons, selectedTeacherId]
  );

  const teacherLedger = useMemo(() =>
    ledger.filter(item => item.teacher_id === selectedTeacherId),
    [ledger, selectedTeacherId]
  );

  // Unpaid completed lessons (passed but not paid)
  const completedLessons = useMemo(() => {
    if (!selectedTeacherId) return []
    const now = new Date();
    return teacherLessons.filter(lesson =>
      lesson.status !== 'canceled' &&
      new Date(lesson.time) < now &&
      !teacherLedger.some(l =>
        l.description.includes(lesson.id) ||
        l.student_id === lesson.student
      )
    );
  }, [teacherLessons, teacherLedger]);

  // Upcoming confirmed lessons (future revenue)
  const upcomingLessons = useMemo(() => {
    const now = new Date();
    return teacherLessons.filter(lesson =>
      lesson.status === 'confirmed' &&
      new Date(lesson.time) > now
    );
  }, [teacherLessons]);

  useMemo(()=>{
    if(state.userRole == 'teacher'){
      setSelectedTeacherId(state.userProfile.id);
    }
  },[state.userRole])
  // TRUE OWED AMOUNT: Lessons completed/past → minus ledger payments
  const studentsOwing = useMemo(() => {
    if (!selectedTeacherId) return []
    return calculateDebtPerStudent( students, teacherLessons, teacherLedger);
  }, [teacherLessons, teacherLedger, students]);

  // Calculate totals
  // TODO - set price per session
  const totalPaid = teacherLedger.reduce((sum, item) => item.amount > 0 ? sum + item.amount : sum, 0);
  const totalUnpaid = completedLessons.reduce((sum, lesson) => sum + parseInt(lesson.duration) * pricePerH / 60, 0); // €10/hr
  const totalUpcoming = upcomingLessons.reduce((sum, lesson) => sum + parseInt(lesson.duration) * pricePerH, 0);

  const [unpaidDropdownOpen, setUnpaidDropdownOpen] = useState(false);
  const [upcomingDropdownOpen, setUpcomingDropdownOpen] = useState(false);

  useMemo(() => {
    fetchLessons();
    fetchTeachers();
  }, []);

  useMemo(() => {
    fetchMyLedger(selectedTeacherId);
  }, [selectedTeacherId]);

  return (
    <div className="fade-in max-w-md mx-auto pt-4 min-h-screen bg-gradient-to-b">
      
      <div className="flex items-center gap-5 mb-4 ps-2 pe-2">
        <ReceiptText className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" />
        <div>
          <h1 className="text-xl font-bold">Wallet</h1>
          <p className="text-text-light text-sm">Transactions this month</p>
        </div>
        {/* button (add new) */}
        <div className="ml-auto">
          <button
            className="btn bg-light shadow-md rounded-full p-2 items-center"
            onClick={() => navigate('/lesson/create')}
          >
            <PlusCircle className="w-6 h-6 mr-0" />
          </button>
        </div>
      </div>


      {/* Teacher select */}
      { state.userRole === 'admin' && (
        <div className="card p-4 mb-4">
          <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
            <Users className="w-4 h-4" />
            Teacher
          </label>
          <select
            className="w-full px-3 py-2 border border-border rounded-xl bg-background"
            value={selectedTeacherId}
            onChange={(e) => setSelectedTeacherId(e.target.value)}
          >
            <option value="">Select a teacher</option>
            {state.teachers.map(t => (
              <option key={t.id} value={t.id}>
                {t.first_name + ' ' + t.last_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* TOP: Unpaid & Upcoming Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

        {/* Paid, Owed and NET value */}
        <div className="card p-6 relative group">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setUnpaidDropdownOpen(!unpaidDropdownOpen)}
          >
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-bold">Paid</p>
                <span className="text-lg font-bold text-emerald-600">€{totalPaid.toFixed(0)}</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-bold">Owed</p>
                <span className="text-lg font-bold text-red-400">€{totalUnpaid.toFixed(0)}</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-bold">NET</p>
                <span className="text-lg font-bold">€{(totalPaid - totalUnpaid).toFixed(0)}</span>
              </div>

            </div>
          </div>

        </div>

        {/* Recent Lessons (RED) */}
        {/* <div className="card p-6 relative group">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setUnpaidDropdownOpen(!unpaidDropdownOpen)}
          >
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5" />
                <span className="text-lg font-bold">€{totalUnpaid.toFixed(0)}</span>
              </div>
              <p className="text-sm text-text-light">Recent Lessons</p>
              <p className="text-xs  font-medium">{completedLessons.length} lessons</p>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${unpaidDropdownOpen ? 'rotate-180' : ''}`} />
          </div>

          {unpaidDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-border rounded-xl shadow-2xl z-[999] max-h-64 overflow-y-auto p-3">
              {completedLessons.slice(0, 5).map(lesson => (
                <div
                  key={lesson.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-all"
                  onClick={() => navigate(`/lesson/${lesson.id}`)}
                >
                  <div>
                    <div className="font-medium flex align-center">
                        {lesson.student}


                      {lesson.status !== 'completed' &&
                      <div className='ml-2 pt-1 text-xs text-text-light'>
                        ⚠️ Complete the session!
                      </div>}
                    </div>
                    <p className="text-xs text-text-light">
                      {new Date(lesson.time).toLocaleDateString()} - {lesson.duration}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">
                      €{(parseInt(lesson.duration) * pricePerH / 60).toFixed(1)}
                    </p>
                  </div>
                </div>
              ))}
              {completedLessons.length > 5 && (
                <p className="text-xs text-center text-text-light p-2">+{completedLessons.length - 5} more</p>
              )}
            </div>
          )}
        </div> */}


      </div>

      {/* STUDENTS OWING MONEY - TRUE BALANCE */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Student Balances
        </h2>
        <div className="space-y-3">
          {studentsOwing.length === 0 ? (
            <div className="card p-8 text-center text-emerald-500">
              {selectedTeacherId && (<>
                
                No data.
              </>)}
              {!selectedTeacherId && (<>
                Select a teacher
              </>)}
            </div>
          ) : (
            studentsOwing.map(({ student, amountOwed, owedLessons }) => (
              <div key={student?.first_name + ' ' + student?.last_name} className="card p-3 py-1 flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white p-2 ${
                    amountOwed > 0 ? 'bg-red-400' :
                    amountOwed < 0 ? 'bg-emerald-500' :
                    'bg-gray-500'
                  }`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="">{student?.first_name + ' ' + student?.last_name}</p>
                    <div className="flex items-center gap-4 text-sm text-text-light">
                      {/* email button to create mailto link and content */}
                      <span className=''>
                        {student?.email && (
                          <a
                            href={`mailto:${student.email}?subject=Regarding your lessons&body=Hi ${student.first_name},%0D%0A%0D%0AThis is a reminder that you owe €${Math.abs(amountOwed).toFixed(2)} for lesson(s). Please make the payment at your earliest convenience. %0D%0A%0D%0A You can check your balance at any time here:${window.location.origin} %0D%0A%0D%0AThank you!`}
                            className="flex items-center gap-1 text-blue-500 hover:underline"
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Amount + Status */}

                <div
                  className="text-right space-y-1"
                  onClick={() => navigate(`/students/${student?.id}`)}
                >
                  <p className={`text-2xl font-bold ${
                    amountOwed > 0 ? 'text-red-400' :
                    amountOwed < 0 ? 'text-emerald-600' :
                    'text-gray-600'
                  }`}>
                    {amountOwed > 0 ? '' : amountOwed < 0 ? '+' : ''}€{Math.abs(amountOwed).toFixed(2)}
                  </p>
                  <p className={`text-xs font-medium ${
                    amountOwed > 0 ? 'text-red-500' :
                    amountOwed < 0 ? 'text-emerald-500' :
                    'text-gray-500'
                  }`}>
                    {amountOwed > 0 ? 'OWES' : amountOwed < 0 ? 'OVERPAID' : 'PAID'}
                  </p>

                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* RECENT TRANSACTIONS */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Transactions ({teacherLedger.length})
          </h2>
          <div
            className="flex"
            onClick={() => navigate('/alltransactions')}>
            See all
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
        <div className="space-y-3">
          {ledger.slice(0, 10).map((item) => {
            const student = students.find(s => s.id === item.student_id);
            return (
              <div key={item.id } className="card p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-semibold">
                    {student ? `${student.first_name} ${student.last_name}` : 'Unknown Student'}
                  </div>
                  <div className="text-xs text-text-light">{new Date(item.date).toLocaleDateString()}</div>
                  <div className="text-xs text-text-light">{item.description}</div>
                </div>
                <div className={`font-bold text-lg ${
                  item.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {item.amount > 0 ? '+' : '-'}€{Math.abs(item.amount).toFixed(2)}
                </div>
              </div>
            );
          })}
          {ledger.length === 0 && (
            <div className="card p-8 text-center text-text-light">
              No transactions yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ledger;

export function calculateDebtPerStudent(students: Student[], lessons:Lesson[], ledger:LedgerEntry[]) {
  if (!students || students.length == 0 ) {return []}
  // if (!selectedTeacherId) return []
    const now = new Date();
    // 1. Calculate owed lessons per student (past + completed/pending)
    const lessonsOwedByStudent: Record<string, { lessons: Lesson[], total: number }> = {};

    lessons.forEach(lesson => {
      const lessonTime = new Date(lesson.time);
      const lessonEnd = new Date(lessonTime);
      lessonEnd.setMinutes(lessonEnd.getMinutes() + parseInt(lesson.duration));

      // Past lessons OR completed lessons (regardless of time)
      const isPastOrCompleted = lessonEnd < now || lesson.status === 'completed';

      if (isPastOrCompleted && lesson.status !== 'canceled') {
        const studentId = lesson.student_id;
        const lessonValue = parseInt(lesson.duration) * 20 / 60; // €20/hour

        if (!lessonsOwedByStudent[studentId]) {
          lessonsOwedByStudent[studentId] = { lessons: [], total: 0 };
        }
        lessonsOwedByStudent[studentId].lessons.push(lesson);
        lessonsOwedByStudent[studentId].total += lessonValue;
      }
    });

    // 2. Adjust with ledger payments (payments reduce owed amount)
    Object.keys(lessonsOwedByStudent).forEach(studentId => {
      const payments = ledger
        .filter(item =>
          item.student_id === studentId &&
          item.amount > 0 // Positive = payment received
        )
        .reduce((sum, item) => sum + item.amount, 0);
      const student = students.filter(s => s.id == studentId)[0];
      if(student && 'balance' in student){
          lessonsOwedByStudent[studentId].total -= (payments + ( student.balance ));
      }else{
        console.error("calculateDebtPerStudent: could not get student for student or balance", student)
      }
    });

    // 3. Convert to final list (only students who owe or overpaid)
    return Object.entries(lessonsOwedByStudent)
      .map(([studentId, data]) => {
        // console.log(studentId)
        const student = students.filter(s => s.id === studentId)[0];
        return {
          student: student,
          owedLessons: data.lessons.length,
          amountOwed: data.total,
          lessons: data.lessons
        };
      })
      // .filter(({ student }) => student)
      .sort((a, b) => Math.abs(b.amountOwed) - Math.abs(a.amountOwed));
}
